import React, { useState, useEffect } from 'react';
import { X, Sparkles, Megaphone, AlertTriangle, Camera, Layout, Shield, Bell, ArrowRight, FileText, RefreshCw, EyeOff, Lightbulb, Send, CheckCircle, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const ICON_MAP = {
  camera: Camera,
  layout: Layout,
  shield: Shield,
  file: FileText,
  refresh: RefreshCw,
  bell: Bell,
  sparkles: Sparkles,
};

const TYPE_CONFIG = {
  update: {
    gradient: 'from-blue-600 to-indigo-700',
    icon: Sparkles,
    label: "What's New",
    accent: 'blue',
    // Explicit classes — Tailwind JIT only scans literal strings.
    itemBg: 'bg-blue-50/40',
    itemBorder: 'border-blue-100',
    itemHover: 'hover:bg-blue-50 hover:border-blue-200',
    itemOpen: 'bg-blue-50 border-blue-200',
    itemIconBg: 'bg-blue-50',
    itemIconColor: 'text-blue-600',
    itemCta: 'text-blue-600',
    itemChevron: 'text-blue-500',
    itemDetailBorder: 'border-blue-300',
  },
  notice: {
    gradient: 'from-amber-500 to-orange-600',
    icon: AlertTriangle,
    label: 'Notice',
    accent: 'amber',
    itemBg: 'bg-amber-50/40',
    itemBorder: 'border-amber-100',
    itemHover: 'hover:bg-amber-50 hover:border-amber-200',
    itemOpen: 'bg-amber-50 border-amber-200',
    itemIconBg: 'bg-amber-50',
    itemIconColor: 'text-amber-600',
    itemCta: 'text-amber-600',
    itemChevron: 'text-amber-500',
    itemDetailBorder: 'border-amber-300',
  },
  admin_message: {
    gradient: 'from-emerald-600 to-teal-700',
    icon: Megaphone,
    label: 'Message',
    accent: 'emerald',
    itemBg: 'bg-emerald-50/40',
    itemBorder: 'border-emerald-100',
    itemHover: 'hover:bg-emerald-50 hover:border-emerald-200',
    itemOpen: 'bg-emerald-50 border-emerald-200',
    itemIconBg: 'bg-emerald-50',
    itemIconColor: 'text-emerald-600',
    itemCta: 'text-emerald-600',
    itemChevron: 'text-emerald-500',
    itemDetailBorder: 'border-emerald-300',
  },
};

// sessionStorage flag → "have we already shown the announcement modal in this
// login session?". Cleared on logout (AuthContext) and when the browser/tab
// closes. This prevents the modal from re-popping every time the user
// navigates back to the dashboard.
const SESSION_KEY = 'whatsNewShownThisSession';

export function WhatsNewModal() {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [featureTitle, setFeatureTitle] = useState('');
  const [featureDesc, setFeatureDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Track which items (by "announcementIndex:itemIndex") have their details
  // expanded. Reset when the user advances to the next announcement.
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    // If we've already shown the modal this login session, skip the fetch.
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    fetchUnread();
  }, []);

  const fetchUnread = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      if (!token) return;
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/unread`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success && res.data.data?.length > 0) {
        setAnnouncements(res.data.data);
        setVisible(true);
        // Mark as shown for this session — even if the user closes via X
        // (without server-side dismiss), it won't re-pop this session.
        sessionStorage.setItem(SESSION_KEY, '1');
      } else {
        // Nothing to show — mark anyway so we don't re-poll on every navigation.
        sessionStorage.setItem(SESSION_KEY, '1');
      }
    } catch {}
  };

  // X button → truly close the entire modal for this session.
  // (Pending announcements remain unread on the server; they'll re-appear next login.)
  const closeAll = () => {
    setVisible(false);
  };

  // "Next / Got It" button → advance to the next announcement, or close on the last one.
  const advanceOrClose = () => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setExpandedItems({}); // Reset expanded details for the next announcement
    } else {
      setVisible(false);
    }
  };

  const dontShowAgain = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/dismiss-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {}
    setVisible(false);
  };

  const submitFeatureRequest = async () => {
    if (!featureTitle.trim()) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('dentalToken');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/feature-request`,
        { title: featureTitle.trim(), description: featureDesc.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
      setFeatureTitle('');
      setFeatureDesc('');
    } catch {}
    setSubmitting(false);
  };

  if (!visible || announcements.length === 0) return null;

  const current = announcements[currentIndex];
  const config = TYPE_CONFIG[current.type] || TYPE_CONFIG.update;
  const HeaderIcon = config.icon;
  const hasMultiple = announcements.length > 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="whats-new-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`bg-gradient-to-r ${config.gradient} px-6 py-5 flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <HeaderIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium uppercase tracking-wider">{config.label}</p>
                <h2 className="text-white font-bold text-lg leading-tight">{current.title}</h2>
              </div>
            </div>
            <button onClick={closeAll} className="text-white/60 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="whats-new-close">
              <X className="h-5 w-5" />
            </button>
          </div>
          {current.senderName && current.type === 'admin_message' && (
            <p className="text-white/80 text-sm mt-2">From: <strong>{current.senderName}</strong></p>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {current.version && (
              <span className="inline-block px-2.5 py-0.5 bg-white/15 text-white text-xs font-medium rounded-full">v{current.version}</span>
            )}
            {current.createdAt && (
              <span
                className="inline-block px-2.5 py-0.5 bg-white/15 text-white text-xs font-medium rounded-full"
                data-testid="whats-new-created-date"
              >
                Released {new Date(current.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Announcement Items */}
          <div className="px-6 py-5">
            <ul className="space-y-2">
              {current.items?.map((item, i) => {
                const ItemIcon = ICON_MAP[item.icon] || ArrowRight;
                const expandKey = `${currentIndex}:${i}`;
                const isExpanded = !!expandedItems[expandKey];
                const hasDetails = !!item.details;

                const toggle = () => {
                  if (!hasDetails) return;
                  setExpandedItems((prev) => ({ ...prev, [expandKey]: !prev[expandKey] }));
                };

                return (
                  <li
                    key={i}
                    className={`rounded-lg border transition-all ${
                      hasDetails
                        ? `${config.itemBg} ${config.itemBorder} ${config.itemHover} cursor-pointer`
                        : 'border-transparent'
                    } ${isExpanded ? config.itemOpen : ''}`}
                  >
                    <button
                      type="button"
                      onClick={toggle}
                      disabled={!hasDetails}
                      className="w-full flex items-start gap-3 p-2.5 text-left"
                      data-testid={`whats-new-item-${i}`}
                      aria-expanded={isExpanded}
                    >
                      <div className={`w-7 h-7 rounded-lg ${config.itemIconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <ItemIcon className={`h-3.5 w-3.5 ${config.itemIconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 leading-relaxed">{item.text}</p>
                        {hasDetails && !isExpanded && (
                          <p className={`text-[11px] font-medium ${config.itemCta} mt-1 flex items-center gap-1`}>
                            How this helps your practice
                            <ChevronDown className="h-3 w-3" />
                          </p>
                        )}
                      </div>
                      {hasDetails && (
                        <ChevronDown
                          className={`h-4 w-4 ${config.itemChevron} flex-shrink-0 mt-1.5 transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>
                    {hasDetails && isExpanded && (
                      <div
                        className={`px-3 pb-3 pt-0.5 ml-10 pr-4 border-l-2 ${config.itemDetailBorder}`}
                        data-testid={`whats-new-item-${i}-details`}
                      >
                        <p className="text-xs text-gray-600 leading-relaxed">{item.details}</p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Request a Feature Section */}
          <div className="px-6 pb-4 border-t border-gray-100 pt-4">
            {!showFeatureForm && !submitted ? (
              <button
                onClick={() => setShowFeatureForm(true)}
                className="w-full flex items-center gap-3 p-3.5 bg-purple-50 border border-dashed border-purple-200 rounded-xl hover:bg-purple-100 hover:border-purple-300 transition-all"
                data-testid="whats-new-request-feature-btn"
              >
                <Lightbulb className="h-5 w-5 text-purple-600 flex-shrink-0" />
                <div className="text-left">
                  <p className="font-semibold text-purple-900 text-sm">Request a New Feature</p>
                  <p className="text-xs text-purple-500">Have an idea? We'd love to hear it.</p>
                </div>
              </button>
            ) : submitted ? (
              <div className="text-center py-3" data-testid="whats-new-feature-submitted">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-gray-800 text-sm">Thank You!</p>
                <p className="text-xs text-gray-500">Dr. Ganz will review your request personally.</p>
              </div>
            ) : (
              <div data-testid="whats-new-feature-form">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-4 w-4 text-purple-600" />
                  <span className="font-semibold text-purple-900 text-sm">Request a New Feature</span>
                </div>
                <input
                  value={featureTitle}
                  onChange={e => setFeatureTitle(e.target.value)}
                  placeholder="What feature would you like?"
                  className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none mb-2"
                  data-testid="whats-new-feature-title"
                />
                <textarea
                  value={featureDesc}
                  onChange={e => setFeatureDesc(e.target.value)}
                  placeholder="Tell us more (optional)..."
                  rows={2}
                  className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none mb-2"
                  data-testid="whats-new-feature-desc"
                />
                <div className="flex items-center gap-2">
                  <Button onClick={submitFeatureRequest} disabled={submitting} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-4">
                    {submitting ? 'Sending...' : <><Send className="h-3 w-3 mr-1" />Submit</>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowFeatureForm(false)} className="text-xs text-gray-500">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <button onClick={dontShowAgain} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors" data-testid="whats-new-dont-show">
            <EyeOff className="h-3 w-3" />
            Don't show again
          </button>
          <div className="flex items-center gap-2">
            {hasMultiple && (
              <p className="text-xs text-gray-400 mr-2">{currentIndex + 1} of {announcements.length}</p>
            )}
            <Button onClick={advanceOrClose} className={`bg-gradient-to-r ${config.gradient} text-white hover:opacity-90 px-6`} data-testid="whats-new-dismiss">
              {hasMultiple && currentIndex < announcements.length - 1 ? 'Next' : 'Got It'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
